"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenWien } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Wien() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Wien verkaufen: So einfach geht’s</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Wien.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Wien"
        link="/auto-verkaufen/wien"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/wien"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Dein Auto in Wien verkaufen - warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Wenn du dein Auto in Wien verkaufen möchtest, stehst du vor
                    einigen Herausforderungen. Der Markt in der Hauptstadt bringt
                    zwar eine hohe Nachfrage nach Gebrauchtwagen mit sich,
                    besonders nach Klein- und Mittelklassewagen für Pendler und
                    Studierende. Gleichzeitig ist der Verkaufsprozess
                    zeitintensiv und oft von Unsicherheiten geprägt. Viele
                    Fahrzeuge in Wien sind typische Kurzstreckenwagen,
                    Leasingrückläufer oder haben bereits Abnutzungsspuren. Dazu
                    kommen Preisverhandlungen, Inserate und das Risiko
                    unseriöser Angebote.
                  </p>
                  <p>
                    Mit Autoeinfachlos wird der Autoverkauf in Wien für dich
                    schnell, transparent und unkompliziert.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Wien - worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Der Wiener Automarkt hat seine Eigenheiten. SUVs sind zwar
                    beliebt, aber besonders gefragt sind wendige Kleinwagen und
                    zuverlässige Mittelklassefahrzeuge, die im Stadtverkehr
                    wenig verbrauchen und leicht zu parken sind.
                  </p>
                  <p>
                    Gleichzeitig gibt es typische Stolperfallen beim Auto
                    verkaufen in Wien:
                  </p>
                  <ul className="custom-unordered-list">
                    <li>Zeitaufwand durch Inserate, Anfragen und Probefahrten.</li>
                    <li>
                      Unsicherheit bei der Preisfindung – was ist dein Auto in
                      Wien wirklich wert?
                    </li>
                    <li>
                      Unzuverlässige Käufer und das Risiko von Zahlungsausfällen.
                    </li>
                  </ul>
                  <p>
                    Genau hier setzt Autoeinfachlos an: Wir nehmen dir die Arbeit
                    ab und verbinden dich mit seriösen, geprüften Händlern in
                    Wien und ganz Österreich.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Wien</h3>
                  <p>
                    Mit Autoeinfachlos verkaufst du dein Auto in Wien schnell und
                    ohne Risiko:
                  </p>
                  <ul className="custom-unordered-list">
                    <li>
                      Zugang zu einem Händlernetzwerk mit über 5.000 Partnern,
                      auch in Wien
                    </li>
                    <li>
                      Auktion statt mühsamer Einzelverhandlung, mehrere Händler
                      geben Gebote ab
                    </li>
                    <li>Abholung direkt bei dir in Wien</li>
                    <li>sichere und schnelle Zahlung</li>
                  </ul>
                  <p>So erzielst du einen fairen Preis und sparst Zeit und Nerven.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Wien - privat oder Händler?"
              rightSecondText={
                <>
                  <p>
                    Viele überlegen zuerst, ihr Auto privat zu verkaufen. Doch
                    beim privaten Autoverkauf in Wien warten gleich mehrere
                    Nachteile:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>Inserate auf Plattformen kosten Zeit und Geld.</li>
                    <li>Ständige Anfragen, Absagen oder unseriöse Käufer.</li>
                    <li>
                      Risiko von Preisdrückerei oder nicht eingehaltenen
                      Terminen.
                    </li>
                  </ul>
                  <p>
                    Im Vergleich dazu bietet der Autoankauf über unser
                    Händlernetzwerk entscheidende Vorteile:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>Geprüfte, seriöse Käufer.</li>
                    <li>Schnelle und sichere Abwicklung.</li>
                    <li>Keine versteckten Kosten, keine Unsicherheiten.</li>
                  </ul>
                  <p>
                    Mit Autoeinfachlos musst du dich nicht zwischen „privat“ und
                    „Händler“ entscheiden. Du profitierst von beidem: der
                    Reichweite vieler Interessenten und der Professionalität
                    geprüfter Autohändler.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Wien wert?"
                alt="Auto in Wien bewerten"
                text={
                  <>
                    <p>
                      Der Preis ist beim Autoverkauf in Wien entscheidend. Er
                      hängt ab von:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>Marke und Modell</li>
                      <li>Baujahr und Kilometerstand</li>
                      <li>
                        Zustand und mögliche Schäden wie Unfall, Motorschaden
                        oder Getriebeschaden
                      </li>
                      <li>aktueller Nachfrage in Wien</li>
                    </ul>

                    <p>
                      Bei Autoeinfachlos kannst du dein Auto kostenlos bewerten
                      lassen. In wenigen Sekunden erhältst du eine Einschätzung
                      und kannst die Auktion starten.
                    </p>
                    <p>
                      👉{" "}
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Wien</h3>
                    <p>Der Ablauf ist einfach, transparent und auf Dich zugeschnitten:</p>
                    <ul className="custom-unordered-list">
                      <li>Auto online bewerten lassen – kostenlos und unverbindlich.</li>
                      <li>
                        Auktion starten – Dein Auto wird in unserem Händlernetzwerk
                        präsentiert.
                      </li>
                      <li>
                        Höchstgebot erhalten – mehrere Händler bieten, du
                        entscheidest.
                      </li>
                      <li>
                        Abholung vor Ort in Wien – Dein Auto wird direkt bei dir
                        in Wien übernommen.
                      </li>
                      <li>Sichere Bezahlung – schnell und unkompliziert.</li>
                    </ul>
                    <p>
                      Damit sparst du Zeit, verhinderst unnötigen Stress und
                      erhältst den besten Preis für dein Fahrzeug.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen rund um den Autoverkauf in Wien"
              fragen={fragenWien}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Wien"
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Wien war noch nie so einfach"
                alt="Auto verkaufen in Wien war noch nie so einfach"
                text={
                  <>
                    <p>
                      Wenn du dein Auto in Wien verkaufen möchtest, musst du dich
                      nicht mit endlosen Inseraten, Preisverhandlungen oder
                      unseriösen Angeboten herumschlagen. Mit Autoeinfachlos
                      profitierst du von einem starken Händlernetzwerk, regionaler
                      Abwicklung und einem transparenten Auktionsverfahren.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten & verkaufen
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Wien;
